import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { db } from '../../utils/firebase';
import { collection, query, where, orderBy, onSnapshot, limit, getDocs, writeBatch, QueryDocumentSnapshot } from 'firebase/firestore';
import { PortalActivity, PortalActivityType, PortalStakeholderLink } from '../../types';
import {
  fetchHistoricalDownloadActivities,
  mergePortalActivityWithHistoricalDownloads,
} from '../../utils/portalHistoricalDownloads';
import {
  Eye,
  MousePointerClick,
  Zap,
  Heart,
  CreditCard,
  FileSignature,
  Upload,
  MessageSquare,
  Clock,
  Activity,
  Loader2,
  Trash2,
  Users,
  Plus,
  Check,
  Copy,
  Download,
  Bot,
  LayoutGrid,
} from 'lucide-react';
import './PortalActivityTimeline.css';

/** Optional — staff invite links for view-only portal access */
export interface PortalActivityStakeholderInviteProps {
  links: Array<PortalStakeholderLink & { id: string }>;
  newName: string;
  onNewNameChange: (v: string) => void;
  busy: boolean;
  copiedLinkId: string | null;
  onCreate: () => void | Promise<void>;
  onRevoke: (linkDocId: string) => void;
  onCopyUrl: (linkDocId: string) => void;
}

interface PortalActivityTimelineProps {
  portalId: string;
  projectId?: string | null;
  projects?: Record<string, { projectName: string }>;
  /** Extra deal ids (e.g. legacy portal.dealId when projects map is incomplete). */
  additionalDealIds?: string[];
  /** Primary portal client — shown on client-sourced rows (All tab and other filters). */
  clientDisplayName?: string;
  stakeholderInvite?: PortalActivityStakeholderInviteProps;
}

/** Activity type filters plus source filters for stakeholder sessions and AI-agent events */
type FilterKey = 'all' | PortalActivityType | 'stakeholder' | 'agent';

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'page_view', label: 'Views' },
  { key: 'link_click', label: 'Clicks' },
  { key: 'action', label: 'Actions' },
  { key: 'stakeholder', label: 'Stakeholders' },
  { key: 'agent', label: 'Agents' },
];

function actionIcon(action: string, type: PortalActivityType, source?: PortalActivity['source']) {
  if (source === 'agent') return { Icon: Bot, cls: 'pat-icon-agent' };
  if (action.includes('inspiration_board')) return { Icon: LayoutGrid, cls: 'pat-icon-view' };
  if (action.includes('download')) return { Icon: Download, cls: 'pat-icon-download' };
  if (action.includes('payment') || action.includes('checkout')) return { Icon: CreditCard, cls: 'pat-icon-payment' };
  if (action.includes('signed') || action.includes('sign_modal')) return { Icon: FileSignature, cls: 'pat-icon-sign' };
  if (action.includes('upload')) return { Icon: Upload, cls: 'pat-icon-upload' };
  if (action.includes('note')) return { Icon: MessageSquare, cls: 'pat-icon-note' };
  if (type === 'heartbeat') return { Icon: Heart, cls: 'pat-icon-heartbeat' };
  if (type === 'link_click') return { Icon: MousePointerClick, cls: 'pat-icon-click' };
  if (type === 'page_view') return { Icon: Eye, cls: 'pat-icon-view' };
  return { Icon: Zap, cls: 'pat-icon-action' };
}

function engagementPageLabel(page?: string): string {
  if (page === 'proposal') return 'proposal';
  if (page === 'change_order') return 'change order';
  if (page === 'portal_pdf') return 'PDF review';
  if (page === 'portal_3d_model') return '3D model review';
  if (page === 'portal_inspiration_board') return 'inspiration board';
  return 'portal';
}

function inferEngagementPage(action: string, metadata?: Record<string, any>): string {
  if (metadata?.page) return String(metadata.page);
  if (action.includes('inspiration_board')) return 'portal_inspiration_board';
  if (action.includes('proposal') || action === 'returned_to_portal') return 'proposal';
  return 'portal';
}

function describeAction(
  ev: PortalActivity,
  projectName?: string,
  durationHint?: number,
): React.ReactNode {
  const proj = projectName ? <span className="pat-event-meta-tag">{projectName}</span> : null;
  const duration =
    typeof durationHint === 'number' && durationHint > 0
      ? durationHint
      : typeof ev.metadata?.secondsOnPage === 'number' && ev.metadata.secondsOnPage > 0
        ? ev.metadata.secondsOnPage
        : undefined;

  switch (ev.action) {
    case 'portal_opened':
      return <><strong>Opened portal</strong>{proj}</>;
    case 'portal_closed':
      return <><strong>Left portal</strong>{duration ? <> · {formatDuration(duration)}</> : null}{proj}</>;
    case 'proposal_opened':
      return <><strong>Opened proposal</strong>{proj}</>;
    case 'proposal_left':
      return <><strong>Left proposal</strong>{duration ? <> · {formatDuration(duration)}</> : null}{proj}</>;
    case 'project_selected':
      return (
        <>
          <strong>Selected project</strong>{' '}
          {ev.metadata?.projectName || projectName || ''}
          {proj}
        </>
      );
    case 'returned_to_project_list':
      return <><strong>Returned to all projects</strong>{proj}</>;
    case 'viewed_project_list':
      return (
        <>
          <strong>Viewed project list</strong>
          {typeof ev.metadata?.projectCount === 'number' ? <> &middot; {ev.metadata.projectCount} projects</> : null}
        </>
      );
    case 'viewed_proposal':
      return <>Viewed <strong>proposal</strong>{duration ? <> · {formatDuration(duration)}</> : null}{proj}</>;
    case 'downloaded_proposal':
      return (
        <>
          Downloaded <strong>proposal PDF</strong>
          {typeof ev.metadata?.totalAmount === 'number' ? (
            <> · ${Number(ev.metadata.totalAmount).toLocaleString()}</>
          ) : null}
          {proj}
        </>
      );
    case 'downloaded_signed_proposal':
      return (
        <>
          Downloaded <strong>signed proposal PDF</strong>
          {typeof ev.metadata?.totalAmount === 'number' ? (
            <> · ${Number(ev.metadata.totalAmount).toLocaleString()}</>
          ) : null}
          {proj}
        </>
      );
    case 'returned_to_portal':
      return <><strong>Returned to portal</strong> from proposal{duration ? <> · {formatDuration(duration)} on proposal</> : null}{proj}</>;
    case 'opened_sign_modal':
      return <><strong>Opened sign &amp; pay</strong>{proj}</>;
    case 'signed_agreement':
      return <><strong>Signed agreement</strong> &middot; ${ev.metadata?.totalAmount?.toLocaleString?.() || '—'}{proj}</>;
    case 'signed_construction_proposal':
      return (
        <>
          <strong>Signed construction proposal</strong>
          {ev.metadata?.signedBy ? <> &middot; {ev.metadata.signedBy}</> : null}
          {typeof ev.metadata?.grandTotal === 'number' ? (
            <> &middot; ${Number(ev.metadata.grandTotal).toLocaleString()}</>
          ) : null}
          {proj}
        </>
      );
    case 'payment_initiated':
      return <><strong>Initiated payment</strong>{proj}</>;
    case 'viewed_signed_agreement':
      return <>Viewed <strong>signed agreement PDF</strong>{proj}</>;
    case 'clicked_proposal':
      return <>Clicked <strong>View Proposal</strong>{proj}</>;
    case 'opened_questionnaire':
      return <><strong>Opened questionnaire</strong>{proj}</>;
    case 'questionnaire_submitted':
      return <><strong>Submitted questionnaire</strong>{proj}</>;
    case 'viewed_3d_model':
      return <>Viewed <strong>3D model</strong>{proj}</>;
    case 'portal_3d_model_opened':
      return (
        <>
          <strong>Opened 3D model review</strong>
          {ev.metadata?.context === 'deliverables_table_external' ? <> · new tab</> : null}
          {ev.metadata?.context === 'design_approval_walkthrough' ? <> · design approval</> : null}
          {proj}
        </>
      );
    case 'portal_3d_model_closed':
      return (
        <>
          <strong>Left 3D model review</strong>
          {ev.metadata?.context === 'deliverables_table_external' ? <> · new tab</> : null}
          {ev.metadata?.context === 'design_approval_walkthrough' ? <> · design approval</> : null}
          {duration ? <> · {formatDuration(duration)}</> : null}
          {proj}
        </>
      );
    case 'opened_inspiration_board':
      return <><strong>Opened inspiration board</strong> from design fee step{proj}</>;
    case 'portal_inspiration_board_opened':
      return <><strong>Opened inspiration board</strong>{proj}</>;
    case 'portal_inspiration_board_closed':
      return (
        <>
          <strong>Left inspiration board</strong>
          {duration ? <> · {formatDuration(duration)}</> : null}
          {proj}
        </>
      );
    case 'portal_pdf_opened':
      return <><strong>Opened PDF review</strong>{ev.metadata?.documentTitle ? <> · {ev.metadata.documentTitle}</> : null}{proj}</>;
    case 'portal_pdf_closed':
      return (
        <>
          <strong>Left PDF review</strong>
          {ev.metadata?.documentTitle ? <> · {ev.metadata.documentTitle}</> : null}
          {duration ? <> · {formatDuration(duration)}</> : null}
          {proj}
        </>
      );
    case 'opened_portal_pdf_markup':
      return <>Opened <strong>PDF markup viewer</strong>{proj}</>;
    case 'pdf_comment_added':
      return (
        <>
          Added <strong>PDF comment</strong>
          {typeof ev.metadata?.pageNumber === 'number' ? <> · page {ev.metadata.pageNumber}</> : null}
          {proj}
        </>
      );
    case 'pdf_markup_added':
      return (
        <>
          Drew <strong>PDF markup</strong>
          {ev.metadata?.tool ? <> · {String(ev.metadata.tool)}</> : null}
          {typeof ev.metadata?.pageNumber === 'number' ? <> · page {ev.metadata.pageNumber}</> : null}
          {proj}
        </>
      );
    case 'opened_design_approval_walkthrough':
      return <><strong>Opened design approval</strong> walkthrough{proj}</>;
    case 'advanced_to_concept_review':
      return <><strong>Continued to 2D drawings</strong> in design approval{proj}</>;
    case 'started_model_walkthrough':
      return <><strong>Started 3D walkthrough</strong> recording{proj}</>;
    case 'submitted_model_walkthrough':
      return <><strong>Submitted 3D walkthrough</strong> to project team{proj}</>;
    case 'approved_3d_model_deliverable':
      return (
        <>
          <strong>Approved 3D model</strong>
          {ev.metadata?.signedBy ? <> · {ev.metadata.signedBy}</> : null}
          {proj}
        </>
      );
    case 'approved_2d_concept_deliverable':
      return (
        <>
          <strong>Approved 2D concept drawings</strong>
          {ev.metadata?.signedBy ? <> · {ev.metadata.signedBy}</> : null}
          {proj}
        </>
      );
    case 'downloaded_concept_elevations':
      return <>Downloaded <strong>2D concept drawings</strong>{proj}</>;
    case 'viewed_3d_loom':
      return <>Watched <strong>design walkthrough</strong> video{proj}</>;
    case 'viewed_2d-plans':
      return <>Viewed <strong>engineered drawings</strong>{proj}</>;
    case 'sent_note':
      return <>Left a <strong>note</strong> on {ev.metadata?.step || 'a step'}{proj}</>;
    case 'uploaded_file':
      return <><strong>Uploaded {ev.metadata?.fileCount || ''} file{(ev.metadata?.fileCount || 0) !== 1 ? 's' : ''}</strong>{proj}</>;
    case 'heartbeat': {
      const page = inferEngagementPage(ev.action, ev.metadata);
      const stale = Date.now() - ev.timestamp.getTime() > 3 * 60_000;
      return (
        <>
          {stale ? 'Was active on' : 'Active on'} {engagementPageLabel(page)}
          {duration ? <> · {formatDuration(duration)}</> : null}
        </>
      );
    }
    // ── AI-agent events (source: 'agent') ──
    case 'enrichment_completed':
      return <><strong>Marketing agent</strong> finished lead enrichment{typeof ev.metadata?.score === 'number' ? <> · fit score {ev.metadata.score}/100</> : null}{proj}</>;
    case 'bidding_analysis_ready':
      return <><strong>Bidding agent</strong> analysis ready{typeof ev.metadata?.itemCount === 'number' ? <> · {ev.metadata.itemCount} timber items</> : null}{proj}</>;
    case 'proposal_reviewed':
      return <><strong>Agent</strong> reviewed the proposal{proj}</>;
    case 'document_synopsis_ready':
      return <><strong>Agent</strong> wrote a synopsis for {ev.metadata?.documentName || 'a document'}{proj}</>;
    default:
      return <>{ev.action.replace(/_/g, ' ')}{proj}</>;
  }
}

function formatDuration(seconds?: number): string {
  if (!seconds || seconds < 1) return '< 1s';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function relativeTime(date: Date): string {
  const now = Date.now();
  const diff = now - date.getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function dateLabel(date: Date): string {
  const now = new Date();
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const t = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = t.getTime() - d.getTime();
  if (diff === 0) return 'Today';
  if (diff === 86_400_000) return 'Yesterday';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: now.getFullYear() !== date.getFullYear() ? 'numeric' : undefined });
}

const POPOVER_W = 300;

const PortalActivityTimeline: React.FC<PortalActivityTimelineProps> = ({
  portalId,
  projectId,
  projects,
  additionalDealIds,
  clientDisplayName,
  stakeholderInvite,
}) => {
  const [events, setEvents] = useState<PortalActivity[]>([]);
  const [historicalDownloads, setHistoricalDownloads] = useState<PortalActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [historicalLoading, setHistoricalLoading] = useState(true);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [showEmployee, setShowEmployee] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [stakeholderPopoverOpen, setStakeholderPopoverOpen] = useState(false);
  const stakeholderBadgeRef = useRef<HTMLButtonElement>(null);
  const stakeholderPopoverRef = useRef<HTMLDivElement>(null);
  const [stakeholderPopPos, setStakeholderPopPos] = useState({ top: 0, left: 0 });

  const activeStakeholderCount = useMemo(() => {
    if (!stakeholderInvite) return 0;
    return stakeholderInvite.links.filter((l) => !l.revokedAt).length;
  }, [stakeholderInvite]);

  const portalDealIds = useMemo(() => {
    const ids = new Set(Object.keys(projects || {}));
    for (const id of additionalDealIds || []) {
      if (id) ids.add(id);
    }
    return Array.from(ids);
  }, [projects, additionalDealIds]);

  const mergedEvents = useMemo(
    () => mergePortalActivityWithHistoricalDownloads(events, historicalDownloads),
    [events, historicalDownloads],
  );

  useLayoutEffect(() => {
    if (!stakeholderPopoverOpen || !stakeholderBadgeRef.current) return;
    const r = stakeholderBadgeRef.current.getBoundingClientRect();
    const left = Math.min(Math.max(8, r.right - POPOVER_W), window.innerWidth - POPOVER_W - 8);
    setStakeholderPopPos({ top: r.bottom + 8, left });
  }, [stakeholderPopoverOpen]);

  useEffect(() => {
    if (!stakeholderPopoverOpen) return;
    const onScroll = () => setStakeholderPopoverOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [stakeholderPopoverOpen]);

  useEffect(() => {
    if (!stakeholderPopoverOpen) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (stakeholderBadgeRef.current?.contains(t)) return;
      if (stakeholderPopoverRef.current?.contains(t)) return;
      setStakeholderPopoverOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setStakeholderPopoverOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [stakeholderPopoverOpen]);

  const handleReset = async () => {
    if (!portalId || resetting) return;
    if (!window.confirm('Clear all activity for this portal? This cannot be undone.')) return;
    setResetting(true);
    try {
      const q = query(collection(db, 'portalActivity'), where('portalId', '==', portalId));
      const snap = await getDocs(q);
      const batchSize = 500;
      for (let i = 0; i < snap.docs.length; i += batchSize) {
        const batch = writeBatch(db);
        snap.docs.slice(i, i + batchSize).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
    } catch (err) {
      console.error('[ActivityTimeline] Reset failed:', err);
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    if (!portalId) return;

    setLoading(true);

    // Two parallel listeners — one for non-staff (client + stakeholder + legacy
    // pre-source rows) and one for staff — each with its own 500-row budget.
    // Heartbeats from staff testing one project shouldn't be able to push the
    // other project's historic client signatures / proposal views past the
    // limit, which is what happens with a single shared listener.
    let nonStaffEvents: PortalActivity[] = [];
    let staffEvents: PortalActivity[] = [];
    let nonStaffReady = false;
    let staffReady = false;

    const mapDoc = (d: QueryDocumentSnapshot): PortalActivity => {
      const data = d.data();
      return {
        id: d.id,
        portalId: data.portalId,
        projectId: data.projectId || undefined,
        sessionId: data.sessionId,
        type: data.type as PortalActivityType,
        action: data.action,
        metadata: data.metadata || undefined,
        userAgent: data.userAgent || undefined,
        source: (data.source as PortalActivity['source']) || 'client',
        timestamp: data.timestamp?.toDate?.() || new Date(),
      };
    };

    const merge = () => {
      // De-dupe in case the same id ever surfaces in both result sets, then sort.
      const byId = new Map<string, PortalActivity>();
      for (const ev of nonStaffEvents) if (ev.id) byId.set(ev.id, ev);
      for (const ev of staffEvents) if (ev.id) byId.set(ev.id, ev);
      const combined = Array.from(byId.values()).sort(
        (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
      );
      setEvents(combined);
      if (nonStaffReady && staffReady) setLoading(false);
    };

    const qNonStaff = query(
      collection(db, 'portalActivity'),
      where('portalId', '==', portalId),
      where('source', 'in', ['client', 'stakeholder', 'agent']),
      orderBy('timestamp', 'desc'),
      limit(500),
    );
    const unsubNonStaff = onSnapshot(
      qNonStaff,
      (snap) => {
        nonStaffEvents = snap.docs.map(mapDoc);
        nonStaffReady = true;
        merge();
      },
      (err) => {
        console.error('[ActivityTimeline] non-staff listener error:', err);
        nonStaffReady = true;
        if (staffReady) setLoading(false);
      },
    );

    const qStaff = query(
      collection(db, 'portalActivity'),
      where('portalId', '==', portalId),
      where('source', '==', 'employee'),
      orderBy('timestamp', 'desc'),
      limit(500),
    );
    const unsubStaff = onSnapshot(
      qStaff,
      (snap) => {
        staffEvents = snap.docs.map(mapDoc);
        staffReady = true;
        merge();
      },
      (err) => {
        console.error('[ActivityTimeline] staff listener error:', err);
        staffReady = true;
        if (nonStaffReady) setLoading(false);
      },
    );

    return () => {
      unsubNonStaff();
      unsubStaff();
    };
  }, [portalId]);

  useEffect(() => {
    if (!portalId || portalDealIds.length === 0) {
      setHistoricalDownloads([]);
      setHistoricalLoading(false);
      return;
    }

    setHistoricalLoading(true);
    let cancelled = false;
    void (async () => {
      try {
        const rows = await fetchHistoricalDownloadActivities(portalDealIds, portalId);
        if (!cancelled) setHistoricalDownloads(rows);
      } catch (err) {
        console.error('[ActivityTimeline] Historical downloads failed:', err);
        if (!cancelled) setHistoricalDownloads([]);
      } finally {
        if (!cancelled) setHistoricalLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [portalId, portalDealIds.join('|')]);

  /** Max active seconds per browser session + surface (portal vs proposal). */
  const sessionPageDuration = useMemo(() => {
    const map = new Map<string, number>();
    for (const ev of mergedEvents) {
      const sec = ev.metadata?.secondsOnPage;
      if (typeof sec !== 'number' || sec < 1) continue;
      const page = inferEngagementPage(ev.action, ev.metadata);
      const key = `${ev.sessionId}:${page}`;
      map.set(key, Math.max(map.get(key) || 0, sec));
    }
    return map;
  }, [mergedEvents]);

  // Collapse consecutive heartbeats from the same session into one session summary
  const processedEvents = useMemo(() => {
    let filtered = mergedEvents;

    // Source filter: hide employee activity unless toggled on
    if (!showEmployee) {
      filtered = filtered.filter(e => e.source !== 'employee');
    }

    // Optional project filter — default is show all projects on multi-project portals.
    if (projectId) {
      filtered = filtered.filter(e => !e.projectId || e.projectId === projectId);
    }

    // Type filter, or source filters for stakeholder / agent rows
    if (filter === 'stakeholder') {
      filtered = filtered.filter((e) => e.source === 'stakeholder');
    } else if (filter === 'agent') {
      filtered = filtered.filter((e) => e.source === 'agent');
    } else if (filter !== 'all') {
      filtered = filtered.filter((e) => e.type === filter);
    }

    // Session open markers are paired with richer view/leave rows.
    filtered = filtered.filter((e) => e.action !== 'proposal_opened');

    // Collapse heartbeats: keep only the latest heartbeat per session + page
    const seenHeartbeatKeys = new Set<string>();
    const result: PortalActivity[] = [];
    for (const ev of filtered) {
      if (ev.action === 'heartbeat') {
        const page = inferEngagementPage(ev.action, ev.metadata);
        const key = `${ev.sessionId}:${page}`;
        if (seenHeartbeatKeys.has(key)) continue;
        seenHeartbeatKeys.add(key);
      }
      result.push(ev);
    }

    return result;
  }, [mergedEvents, projectId, filter, showEmployee]);

  const durationHintForEvent = useCallback(
    (ev: PortalActivity): number | undefined => {
      if (ev.action === 'viewed_proposal') {
        return sessionPageDuration.get(`${ev.sessionId}:proposal`);
      }
      if (ev.action === 'returned_to_portal') {
        return sessionPageDuration.get(`${ev.sessionId}:proposal`);
      }
      return undefined;
    },
    [sessionPageDuration],
  );

  // Group by date
  const grouped = useMemo(() => {
    const groups: { label: string; date: string; events: PortalActivity[] }[] = [];
    let currentKey = '';
    for (const ev of processedEvents) {
      const key = ev.timestamp.toDateString();
      if (key !== currentKey) {
        currentKey = key;
        groups.push({ label: dateLabel(ev.timestamp), date: key, events: [] });
      }
      groups[groups.length - 1].events.push(ev);
    }
    return groups;
  }, [processedEvents]);

  const projectNameMap = useMemo(() => {
    if (!projects) return {};
    const map: Record<string, string> = {};
    for (const [id, p] of Object.entries(projects)) {
      map[id] = p.projectName || 'Project';
    }
    return map;
  }, [projects]);

  const clientLabel = (clientDisplayName || '').trim();

  return (
    <div className="pat-panel">
      <div className="pat-header">
        <div className="pat-header-top">
          <span className="pat-title">
            <Activity size={13} />
            Activity
            <span className="pat-live-dot" />
            <button
              className={`pat-switch ${showEmployee ? 'on' : ''}`}
              onClick={() => setShowEmployee(prev => !prev)}
              aria-label="Include staff activity"
            />
            <span
              className={`pat-source-label ${showEmployee ? 'active' : ''}`}
              onClick={() => setShowEmployee(prev => !prev)}
            >
              Staff
            </span>
          </span>
          <span className="pat-header-right">
            {stakeholderInvite && (
              <>
                <button
                  ref={stakeholderBadgeRef}
                  type="button"
                  className={`pat-stakeholder-badge ${stakeholderPopoverOpen ? 'open' : ''} ${activeStakeholderCount > 0 ? 'has-links' : ''}`}
                  onClick={() => setStakeholderPopoverOpen((o) => !o)}
                  title="Stakeholder links (view only)"
                >
                  <Users size={11} strokeWidth={2.25} />
                  {activeStakeholderCount > 0 && (
                    <span className="pat-stakeholder-badge-count">{activeStakeholderCount}</span>
                  )}
                </button>
                {stakeholderPopoverOpen &&
                  createPortal(
                    <>
                      <div
                        className="pat-stakeholder-backdrop"
                        aria-hidden
                        onClick={() => setStakeholderPopoverOpen(false)}
                      />
                      <div
                        ref={stakeholderPopoverRef}
                        className="pat-stakeholder-popover"
                        style={{ top: stakeholderPopPos.top, left: stakeholderPopPos.left, width: POPOVER_W }}
                        role="dialog"
                        aria-label="Stakeholder links"
                      >
                        <div className="pat-stakeholder-popover-title">
                          <Users size={14} />
                          View-only links
                        </div>
                        <p className="pat-stakeholder-popover-hint">
                          Share with architects, lenders, or family. They see the portal but cannot sign, pay, upload, or submit questionnaires.
                        </p>
                        <div className="pat-stakeholder-popover-row">
                          <input
                            type="text"
                            value={stakeholderInvite.newName}
                            onChange={(e) => stakeholderInvite.onNewNameChange(e.target.value)}
                            placeholder="Name"
                            className="pat-stakeholder-input"
                          />
                          <button
                            type="button"
                            className="pat-stakeholder-create"
                            disabled={stakeholderInvite.busy || !stakeholderInvite.newName.trim()}
                            onClick={() => stakeholderInvite.onCreate()}
                          >
                            {stakeholderInvite.busy ? <Loader2 size={12} className="pat-spin" /> : <Plus size={12} />}
                          </button>
                        </div>
                        {stakeholderInvite.links.length > 0 && (
                          <ul className="pat-stakeholder-list">
                            {stakeholderInvite.links.map((sl) => {
                              const revoked = !!sl.revokedAt;
                              return (
                                <li key={sl.id} className={`pat-stakeholder-li ${revoked ? 'revoked' : ''}`}>
                                  <span className="pat-stakeholder-name">{sl.displayName}</span>
                                  {revoked ? (
                                    <span className="pat-stakeholder-revoked-label">Revoked</span>
                                  ) : (
                                    <span className="pat-stakeholder-li-actions">
                                      <button
                                        type="button"
                                        className="pat-stakeholder-mini-btn"
                                        onClick={() => stakeholderInvite.onCopyUrl(sl.id)}
                                        title="Copy link"
                                      >
                                        {stakeholderInvite.copiedLinkId === sl.id ? <Check size={12} /> : <Copy size={12} />}
                                      </button>
                                      <button
                                        type="button"
                                        className="pat-stakeholder-revoke"
                                        onClick={() => stakeholderInvite.onRevoke(sl.id)}
                                      >
                                        Revoke
                                      </button>
                                    </span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </>,
                    document.body
                  )}
              </>
            )}
            <span className="pat-count">{processedEvents.length}</span>
            <button
              className="pat-reset-btn"
              onClick={handleReset}
              disabled={resetting || events.length === 0}
              title="Clear all activity"
            >
              {resetting ? <Loader2 size={11} className="pat-spin" /> : <Trash2 size={11} />}
            </button>
          </span>
        </div>
        <div className="pat-filters">
          {FILTERS.map(f => (
            <button
              key={f.key}
              className={`pat-filter-chip ${filter === f.key ? 'active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading || historicalLoading ? (
        <div className="pat-loading">
          <Loader2 size={20} />
        </div>
      ) : processedEvents.length === 0 ? (
        <div className="pat-empty">
          <Activity />
          <p>
            {filter === 'stakeholder' ? (
              <>No stakeholder activity yet.<br />Sessions opened via stakeholder links appear here.</>
            ) : (
              <>No client activity yet.<br />Events will appear here in real time.</>
            )}
          </p>
        </div>
      ) : (
        <div className="pat-events">
          {grouped.map(group => (
            <div key={group.date} className="pat-date-group">
              <div className="pat-date-label">{group.label}</div>
              {group.events.map(ev => {
                const { Icon, cls } = actionIcon(ev.action, ev.type, ev.source);
                const projName = ev.projectId
                  ? (projectNameMap[ev.projectId] || (typeof ev.metadata?.projectName === 'string' ? ev.metadata.projectName : undefined))
                  : (typeof ev.metadata?.fromProjectName === 'string' ? ev.metadata.fromProjectName : undefined);
                const stakeholderName =
                  typeof ev.metadata?.stakeholderDisplayName === 'string'
                    ? ev.metadata.stakeholderDisplayName.trim()
                    : '';
                const stakeholderLeadId =
                  typeof ev.metadata?.leadId === 'string' ? ev.metadata.leadId.trim() : '';
                const showClientTag =
                  filter === 'all' && clientLabel && ev.source === 'client';

                return (
                  <div
                    key={ev.id}
                    className={`pat-event ${ev.source === 'employee' ? 'pat-event--employee' : ''} ${ev.source === 'stakeholder' ? 'pat-event--stakeholder' : ''} ${ev.source === 'client' ? 'pat-event--client' : ''} ${ev.source === 'agent' ? 'pat-event--agent' : ''}`}
                  >
                    <div className={`pat-event-icon ${cls}`}>
                      <Icon />
                    </div>
                    <div className="pat-event-body">
                      <div className="pat-event-desc">
                        {describeAction(ev, projName, durationHintForEvent(ev))}
                        {ev.source === 'employee' && <span className="pat-employee-tag">Staff</span>}
                        {ev.source === 'agent' && <span className="pat-agent-tag">Agent</span>}
                        {showClientTag && (
                          <span className="pat-client-tag" title={`${clientLabel} (portal client)`}>
                            {clientLabel}
                          </span>
                        )}
                        {ev.source === 'stakeholder' && (
                          stakeholderLeadId ? (
                            <Link
                              to={`/client/${stakeholderLeadId}`}
                              className="pat-stakeholder-tag"
                              title={`${stakeholderName || 'Stakeholder'} — open lead`}
                              onClick={e => e.stopPropagation()}
                            >
                              {stakeholderName || 'Stakeholder'}
                            </Link>
                          ) : (
                            <span
                              className="pat-stakeholder-tag"
                              title={stakeholderName ? `${stakeholderName} (view-only stakeholder)` : 'View-only stakeholder link'}
                            >
                              {stakeholderName || 'Stakeholder'}
                            </span>
                          )
                        )}
                      </div>
                      <div className="pat-event-time">
                        {relativeTime(ev.timestamp)} &middot; {ev.timestamp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalActivityTimeline;
